import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { CrossPromoCards } from "./CrossPromoCards.js";

export interface PromoModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Bottom-anchored sheet listing the full giglabo product family. Reuses
 * the same `<CrossPromoCards />` component the post-share success modal
 * uses, so the two surfaces stay visually identical.
 *
 * Closes on ESC, on backdrop click, or via the explicit ✕ button. No
 * auto-dismiss — the user opted in by tapping the romb, give them time.
 */
export function PromoModal({ open, onClose }: PromoModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-modal-title"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-t-xl bg-[var(--tg-bg)] p-4 text-[var(--tg-text)] shadow-xl sm:rounded-xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2
              id="promo-modal-title"
              className="text-base font-semibold tracking-tight"
            >
              {t("promo.title")}
            </h2>
            <p className="mt-0.5 text-xs text-[var(--tg-hint)]">
              {t("promo.subtitle")}
            </p>
          </div>
          <button
            type="button"
            aria-label={t("promo.close")}
            onClick={onClose}
            className="-m-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[var(--tg-hint)] hover:bg-[var(--tg-secondary-bg)]"
          >
            <X size={18} />
          </button>
        </div>
        <CrossPromoCards />
      </div>
    </div>
  );
}
